import type { AtsCheck, AtsCheckResult, ExtractedPdf } from './types'
import { detectMultiColumn, groupLines, type Line } from './lines'
import { detectSections } from './sections'
import {
  analyzeBullets,
  analyzeDates,
  countWords,
  detectContact,
  joinLines,
} from './extract'
import { extractResume } from './extract-resume'
import type { ParsedResume } from './types'

// Below this many extracted characters we assume the PDF is a scan/image with
// no text layer: the single most important ATS failure.
const IMAGE_ONLY_CHAR_THRESHOLD = 100

const QUANTIFICATION_RATIO = 0.4
const MIN_WORDS = 150
const MAX_WORDS = 900

export type ScoreInput = {
  lines: Line[]
  fullText: string
  totalChars: number
  pageWidth: number
  wordCount: number
}

function check(
  id: AtsCheck['id'],
  label: string,
  weight: number,
  pass: boolean,
  detail: string,
): AtsCheck {
  return { id, label, weight, pass, detail }
}

export function scoreResume(input: ScoreInput): AtsCheckResult {
  const { lines, fullText, totalChars, pageWidth, wordCount } = input

  // Gate: no readable text layer -> hard fail, everything else is moot.
  if (totalChars < IMAGE_ONLY_CHAR_THRESHOLD) {
    return {
      score: 0,
      isImageOnly: true,
      multiColumn: false,
      wordCount,
      checks: [
        check(
          'readable',
          'Teks terbaca mesin',
          0,
          false,
          'CV kamu kemungkinan berupa gambar atau hasil scan. ATS tidak bisa membaca teksnya sama sekali. Simpan ulang sebagai PDF teks (export dari Word/Docs, bukan foto/scan).',
        ),
      ],
    }
  }

  const sections = detectSections(lines)
  const contact = detectContact(fullText)
  const dates = analyzeDates(fullText)
  const bullets = analyzeBullets(lines)
  const multiColumn = detectMultiColumn(lines, pageWidth)

  const contactPass = contact.email && contact.phone
  const headingsPass = sections.size >= 3
  const datesPass = dates.total > 0 && dates.consistent
  const bulletsPass = bullets.bulletCount >= 3
  const quantPass =
    bullets.bulletCount > 0 && bullets.quantifiedRatio >= QUANTIFICATION_RATIO
  const lengthPass = wordCount >= MIN_WORDS && wordCount <= MAX_WORDS

  const missingContact = [
    !contact.email ? 'email' : null,
    !contact.phone ? 'nomor telepon' : null,
  ]
    .filter(Boolean)
    .join(' & ')

  const checks: AtsCheck[] = [
    check(
      'readable',
      'Teks terbaca mesin',
      0,
      true,
      'CV kamu punya text-layer, jadi ATS bisa membacanya.',
    ),
    check(
      'contact',
      'Kontak lengkap',
      15,
      contactPass,
      contactPass
        ? 'Email dan nomor telepon terdeteksi.'
        : `Belum terdeteksi: ${missingContact || 'kontak'}. Taruh email dan nomor di baris paling atas.`,
    ),
    check(
      'headings',
      'Heading section standar',
      20,
      headingsPass,
      headingsPass
        ? `${sections.size} section standar terbaca.`
        : 'Heading standar belum lengkap. Pakai "Pengalaman Kerja", "Pendidikan", "Keterampilan".',
    ),
    check(
      'singleColumn',
      'Layout satu kolom',
      15,
      !multiColumn,
      multiColumn
        ? 'Terdeteksi layout 2 kolom. Parser ATS sering salah baca kolom. Pakai satu kolom.'
        : 'Layout satu kolom, aman untuk parser.',
    ),
    check(
      'dateConsistency',
      'Format tanggal konsisten',
      10,
      datesPass,
      datesPass
        ? 'Format tanggal konsisten.'
        : dates.total === 0
          ? 'Tidak ada tanggal terdeteksi. Tambah rentang tanggal di tiap pengalaman.'
          : 'Format tanggal campur-campur. Samakan, misalnya "Jan 2024 - Mei 2026".',
    ),
    check(
      'bullets',
      'Pakai bullet point',
      10,
      bulletsPass,
      bulletsPass
        ? 'Pengalaman ditulis dalam bullet, mudah di-parse.'
        : 'Sedikit/tanpa bullet. Tulis pengalaman sebagai bullet, bukan paragraf.',
    ),
    check(
      'quantification',
      'Bullet ada angka/hasil',
      20,
      quantPass,
      quantPass
        ? 'Banyak bullet memuat angka atau hasil konkret.'
        : 'Bullet minim angka. Tambah metrik: "naikkan engagement 38%".',
    ),
    check(
      'length',
      'Panjang CV wajar',
      10,
      lengthPass,
      lengthPass
        ? `Panjang CV ~${wordCount} kata, pas.`
        : wordCount < MIN_WORDS
          ? `CV terlalu pendek (~${wordCount} kata). Tambah detail pengalaman.`
          : `CV terlalu panjang (~${wordCount} kata). Ringkas ke hal paling relevan.`,
    ),
  ]

  const score = checks.reduce(
    (total, item) => total + (item.pass ? item.weight : 0),
    0,
  )

  return {
    score,
    isImageOnly: false,
    multiColumn,
    wordCount,
    checks,
  }
}

export function analyzeExtractedPdf(extracted: ExtractedPdf): AtsCheckResult {
  const lines = groupLines(extracted.items)
  const fullText = joinLines(lines)
  return scoreResume({
    lines,
    fullText,
    totalChars: extracted.totalChars,
    pageWidth: extracted.pageWidth,
    wordCount: countWords(fullText),
  })
}

// Score + OpenResume-style structured extraction in one pass over the lines.
export function analyzeResume(extracted: ExtractedPdf): {
  result: AtsCheckResult
  parsed: ParsedResume
} {
  const lines = groupLines(extracted.items)
  const fullText = joinLines(lines)
  const result = scoreResume({
    lines,
    fullText,
    totalChars: extracted.totalChars,
    pageWidth: extracted.pageWidth,
    wordCount: countWords(fullText),
  })
  const parsed = extractResume(lines)
  return { result, parsed }
}
